/**
 * Pause/play for the theme's perpetual motions (WCAG 2.2.2): the
 * announcement-bar Splide carousel and the CSS-marquee scrolling banner. Both
 * render `snippets/custom-motion-toggle.liquid`; this file drives it, keyed off
 * the button's `data-fb-motion-*` attributes.
 *
 * A plain `defer` script, not an Alpine component: the theme defers most of its
 * JS until the first touch/scroll/mouseover, which a keyboard-only user on a
 * cold load never fires — the exact population 2.2.2 protects. Everything here
 * binds on parse.
 *
 * The announcement bar does not use Splide's own `.splide__toggle`, which would
 * manage `aria-label` / `aria-controls` / `is-active` for free: the Elements
 * component only finds that button *inside* the carousel root, and this root is
 * `overflow-hidden` with both padding gutters occupied by the prev/next arrows
 * at >=1024px. The button sits outside as a flex sibling and those semantics are
 * reproduced below.
 *
 * ⚠ `Autoplay.pause()` with its default argument sets the component's `stopped`
 * flag, which suppresses the `pauseOnHover` / `pauseOnFocus` auto-toggle — so a
 * deliberate pause survives the pointer leaving the bar. `pause(false)` is the
 * internal hover call and does not stick; `play()` clears the flag.
 */
(() => {
  'use strict';

  if (window.__fbA11yMotionControls) return;
  window.__fbA11yMotionControls = true;

  const TOGGLE_SELECTOR = '[data-fb-motion-toggle]';

  /**
   * The marquee's `animation` shorthand reads `var(--play-state, running)`
   * (`.animate-scroll-banner`, `snippets/css-variables.liquid`). Writing it
   * inline on the region is what pauses it, and inline beats the
   * reduced-motion rule in `assets/custom-theme.css.liquid` — so a user who
   * presses Play under `prefers-reduced-motion` gets motion.
   */
  const PLAY_STATE_PROPERTY = '--play-state';

  const prefersReducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isPaused = (button) => button.getAttribute('data-fb-motion-state') === 'paused';

  /** Writes the state the CSS keys its icon swap off, and the matching name. */
  const setState = (button, paused) => {
    button.setAttribute('data-fb-motion-state', paused ? 'paused' : 'playing');

    const label = button.getAttribute(paused ? 'data-fb-motion-label-play' : 'data-fb-motion-label-pause');
    if (label) button.setAttribute('aria-label', label);
  };

  const targetOf = (button) => {
    const selector = button.getAttribute('data-fb-motion-target');
    return selector ? document.querySelector(selector) : null;
  };

  const autoplayOf = (root) => {
    // `assets/theme.js` (xSplide store) assigns `root.splide` before mounting.
    const splide = root && root.splide;
    return (splide && splide.Components && splide.Components.Autoplay) || null;
  };

  const applyMarquee = (region, paused) => {
    region.style.setProperty(PLAY_STATE_PROPERTY, paused ? 'paused' : 'running');
  };

  const applySplide = (root, paused) => {
    const autoplay = autoplayOf(root);
    if (!autoplay) return false;

    if (paused) {
      autoplay.pause();
    } else {
      autoplay.play();
    }
    return true;
  };

  const apply = (button, paused) => {
    const target = targetOf(button);
    if (!target) return;

    if (button.getAttribute('data-fb-motion-type') === 'splide') {
      // A press before Splide mounts cannot reach the autoplay timer; the intent
      // is recorded on the button and `onSplideInit` replays it, so the carousel
      // never starts moving against an explicit pause.
      applySplide(target, paused);
    } else {
      applyMarquee(target, paused);
    }
  };

  /**
   * Runs `callback` every time Splide finishes mounting on `root`, which it
   * marks by adding `is-initialized` at the end of `mount()`.
   *
   * Every time, not once: the carousel mounts from `x-intersect.once` so the
   * first mount can be seconds (or a scroll) away on a cold load, and with
   * `enable_carousel_on_desktop` off the config destroys and re-creates Splide
   * as the viewport crosses 768px — state has to be re-applied per instance.
   */
  const onSplideInit = (root, callback) => {
    let initialized = root.classList.contains('is-initialized');
    if (initialized) callback();

    new MutationObserver(() => {
      const nowInitialized = root.classList.contains('is-initialized');
      if (nowInitialized === initialized) return;

      initialized = nowInitialized;
      if (initialized) callback();
    }).observe(root, { attributes: true, attributeFilter: ['class'] });
  };

  const initSplideToggle = (button, root) => {
    onSplideInit(root, () => {
      // Splide names the track `<rootId>-track` on init; point the control at it
      // now that the id exists.
      const track = root.querySelector('.splide__track');
      if (track && track.id && !button.hasAttribute('aria-controls')) {
        button.setAttribute('aria-controls', track.id);
      }

      // Honour a reduced-motion preference, and replay a press that landed
      // before the carousel existed.
      if (isPaused(button)) applySplide(root, true);
    });
  };

  const initToggle = (button) => {
    const target = targetOf(button);
    if (!target) {
      // Nothing to control. A live-looking button that pauses nothing is its own
      // defect, so remove it rather than ship a no-op.
      button.remove();
      return;
    }

    // Reduced motion starts both components paused, with the button showing Play
    // so the choice stays the user's. Nothing is written to the marquee here:
    // the media query in `custom-theme.css.liquid` already holds it still, and
    // staying out of the inline style is what lets a later Play press win.
    if (prefersReducedMotion()) setState(button, true);

    if (button.getAttribute('data-fb-motion-type') === 'splide') {
      initSplideToggle(button, target);
    }

    button.addEventListener('click', () => {
      const paused = !isPaused(button);
      setState(button, paused);
      apply(button, paused);
    });
  };

  document.querySelectorAll(TOGGLE_SELECTOR).forEach(initToggle);
})();
